/**
 * The book as the PDF export takes it (part 21, table N), read afresh from the store: the theme and metadata the preview
 * lays it out with, and its live chapters in order, each titled as the preview titles it and holding its text as it is
 * now. A book that can't be read whole is no book: the PDF never leaves a chapter out.
 */
import { orderChapters } from '@cockatrice/core';
import { newMetadata } from '@cockatrice/layout';
import type { BookChapter, BookMetadata, PdfBook, Theme } from '@cockatrice/layout';
import type { ChapterEntry, InvalidRecord } from '@cockatrice/storage';

/** What the export reads the book from. */
export interface BookSource {
  /** The open book's ID (part 16b: the images its text names are its own). */
  readonly id: string;
  /** The open book's theme (the default when its record can't be read) and metadata (null then). */
  readonly theme: Theme;
  readonly metadata: BookMetadata | null;
  /** The book's entries as stored, and those the reader refused. */
  readonly entries: { readonly records: readonly ChapterEntry[]; readonly invalid: readonly InvalidRecord[] };
  /** A chapter's stored text: undefined when it has none, null when it can't be read. */
  readonly text: (chapterId: string) => Promise<string | null | undefined>;
  /** The open chapter and its text as typed, or null. */
  readonly open: { readonly chapterId: string; readonly text: string } | null;
  /** The untitled book's title, for a book whose record can't be read (as the preview lays it out). */
  readonly untitled: string;
  /** A title as a rename stores it (`cleanTitle`), which the preview shows. */
  readonly title: (title: string) => string;
}

/** The book as the exports take it, and each chapter's ID with its title and text, in the same order (part 22, table PN; part 23b). */
export interface ExportedBook {
  readonly book: PdfBook;
  readonly chapters: readonly { readonly id: string; readonly title: string; readonly subtitle: string; readonly text: string }[];
}

/**
 * Table N. N2, N3: the theme, and the metadata or the untitled book's; N4, N5: the live chapters in order, titled as the
 * preview titles them, with their IDs; N6: each one's text, the open one's as typed (unsaved typing included), the others'
 * as stored, empty when none is. Null when an entry (N9) or a chapter's text (N7) can't be read.
 */
export async function readForExport(source: BookSource): Promise<ExportedBook | null> {
  if (source.entries.invalid.length > 0) return null;
  const chapters: BookChapter[] = [];
  const ids: { readonly id: string; readonly title: string; readonly subtitle: string; readonly text: string }[] = [];
  const entries = orderChapters(source.entries.records.filter((each) => each.trashed === null));
  // Decision 14: every text asked for at once, so a long book waits on one round of reads, not one per chapter.
  const open = source.open;
  const texts = await Promise.all(entries.map((entry) => (open?.chapterId === entry.id ? Promise.resolve(open.text) : source.text(entry.id))));
  for (const [index, entry] of entries.entries()) {
    const text = texts[index];
    if (text === null) return null;
    chapters.push({ title: source.title(entry.title), subtitle: source.title(entry.subtitle), image: entry.image, text: text ?? '' });
    ids.push({ id: entry.id, title: source.title(entry.title), subtitle: source.title(entry.subtitle), text: text ?? '' });
  }
  return { book: { id: source.id, theme: source.theme, metadata: source.metadata ?? newMetadata(source.untitled), chapters }, chapters: ids };
}

/** Table N: the book as the PDF and EPUB exports take it ({@link readForExport} without the IDs). */
export async function bookForExport(source: BookSource): Promise<PdfBook | null> {
  return (await readForExport(source))?.book ?? null;
}
